import {
  createElement,
  lazy,
  useState,
  type ComponentProps,
  type ComponentType
} from "react";

/**
 * デプロイ直後に発生する「チャンク読み込み失敗」を吸収する React.lazy ラッパー。
 *
 * 背景:
 * - 全ルートを React.lazy で分割しているため、画面遷移のたびに未ロードの
 *   チャンク (/assets/<name>-<hash>.js) を動的 import する。
 * - 本番デプロイ（GitHub Release 連動）が走ると、開きっぱなしの古いタブが保持する
 *   旧ハッシュのチャンク URL は新リリースで消えている。Firebase Hosting の SPA
 *   リライト（"**" -> /index.html）により、存在しない .js への要求には index.html
 *   (HTTP 200 / text/html) が返る。ブラウザはこれを JS モジュールとして解釈できず
 *   "Failed to fetch dynamically imported module" 等で reject する。
 * - 一部環境では import が reject せず `undefined` で resolve し、React.lazy が
 *   `_result.default` を読んで TypeError（reading 'default'）になる。
 * - これらの失敗は Suspense を貫通してルートの AppErrorBoundary に到達し、アプリ全体が
 *   「うまく表示できませんでした」のエラー画面に置き換わる（リロードするまで固定）。
 *
 * 対処:
 * - チャンク読み込み失敗を検知したら、最新の index.html を取り直すために一度だけ
 *   フルリロードして透過的に復帰させる（index.html は Cache-Control: no-cache なので
 *   リロードで確実に最新のハッシュを取得できる）。
 * - リロードしても直らない本物の障害で無限リロードに陥らないよう、sessionStorage で
 *   直近リロードをガードし、クールダウン内の再失敗は error boundary に委ねる。
 */

const RELOAD_GUARD_KEY = "school-ai:chunk-reload-at";
const RELOAD_COOLDOWN_MS = 15_000;

// vite:preloadError の listener と React.lazy の catch は、同じ読み込み失敗に対して
// 続けて呼ばれる。先に listener が reload を予約した場合、後続処理も復旧中として
// 扱わないと、reload 直前の人工エラーが ErrorBoundary / Sentry まで到達してしまう。
// document が実際に reload されればモジュール状態ごと false に戻るため、reload 後も
// 同じ障害が続く場合は sessionStorage の cooldown により従来どおりエラー画面へ進む。
let chunkReloadInProgress = false;

// 先読み（ナビ項目の hover など）で走っている import の数。
// 先読みの失敗は描画時に取り直せばよいので、復帰リロードの対象にしない。
// vite:preloadError の安全網まで反応すると、入力中の画面が hover だけで
// リロードされて消えてしまう
let prefetchInFlight = 0;

const EMPTY_LAZY_MODULE_ERROR =
  "Failed to fetch dynamically imported module: empty lazy module";

/** デプロイ後の stale chunk / 動的 import 失敗を表すエラーかどうかを判定する。 */
export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return (
    // Chrome/Firefox: 動的 import の取得失敗
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    // Safari/WebKit: モジュールスクリプトの読み込み失敗
    /Importing a module script failed/i.test(message) ||
    // Vite が動的importに付随するCSSを先読みできなかった場合
    /Unable to preload CSS/i.test(message) ||
    // webpack 由来の表現（保険）
    /Loading chunk \d+ failed/i.test(message) ||
    /ChunkLoadError/i.test(message) ||
    // MIME 不一致: index.html(text/html) が JS として返された
    /expected a javascript(-or-wasm)? module/i.test(message) ||
    /MIME type of ['"]?text\/html/i.test(message) ||
    // Safari 17+/WebKit の MIME 不一致（Sentry SCHOOL-AI-36 / 4K）:
    //   "'text/html' is not a valid JavaScript MIME type."
    /is not a valid JavaScript MIME type/i.test(message) ||
    // React.lazy が undefined モジュールに .default を読んだとき（Sentry SCHOOL-AI-4V）
    // Chrome: "Cannot read properties of undefined (reading 'default')"
    /Cannot read properties of undefined \(reading ['"]default['"]\)/i.test(
      message
    ) ||
    // Safari: "undefined is not an object (evaluating 'M._result.default')"
    /undefined is not an object \(evaluating ['"][^'"]*_result\.default['"]\)/i.test(
      message
    ) ||
    // Firefox: "M._result is undefined"
    /_result is undefined/i.test(message) ||
    // React 開発ビルド相当のメッセージ
    /lazy: Expected the result of a dynamic import/i.test(message)
  );
}

/**
 * React.lazy が期待する `{ default: Component }` 形であることを保証する。
 * undefined / default 欠落はチャンク失敗として throw し、reload 経路に乗せる。
 */
export function ensureLazyModule<T>(mod: { default: T } | null | undefined): {
  default: T;
} {
  if (mod == null || mod.default == null) {
    throw new Error(EMPTY_LAZY_MODULE_ERROR);
  }
  return mod;
}

/** 直近でチャンク起因のリロードを実行済みか（無限ループ防止のガード）。 */
function hasRecentlyReloaded(): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_GUARD_KEY) ?? "0");
    return Number.isFinite(last) && Date.now() - last < RELOAD_COOLDOWN_MS;
  } catch {
    // sessionStorage が使えない環境ではガードなし（= リロードを試みる）
    return false;
  }
}

function markReloaded(): void {
  try {
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // 保存できなくてもリロード自体は行う
  }
}

/**
 * チャンク失敗からの復帰リロードを一度だけ実行する。
 * この document でリロードを開始済み、または今回開始したら true、
 * 前回のリロード後のクールダウン中で見送ったら false を返す。
 */
export function reloadOnceForChunkError(): boolean {
  if (chunkReloadInProgress) return true;
  if (hasRecentlyReloaded()) return false;
  markReloaded();
  chunkReloadInProgress = true;
  window.location.reload();
  return true;
}

/** React.lazy 相当のコンポーネントに、チャンクの先読み（preload）を足したもの */
export type PreloadableLazyComponent<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends ComponentType<any>
> = ComponentType<ComponentProps<T>> & {
  /**
   * チャンクを前もって取りに行く（ナビ項目の hover・フォーカス・押し始めで呼ぶ）。
   * 失敗しても何も起こさず、描画時に改めて読み直す
   */
  preload: () => void;
};

/**
 * React.lazy 互換のラッパー。動的 import がデプロイ由来のチャンク失敗で reject した
 * 場合、および空モジュールで resolve した場合に一度だけフルリロードして復帰する。
 * それ以外の失敗、およびリロード後の再失敗はそのまま throw して AppErrorBoundary に委ねる。
 *
 * 返すコンポーネントの preload() でチャンクを先読みできる。import() の Promise は
 * 描画と先読みで共有するので、先読みが済んでいれば描画時の読み込み待ちはほぼ無くなる。
 */
export function lazyWithReload<
  // React.lazy と同じシグネチャに合わせる。props の反変性のため unknown ではなく any。
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends ComponentType<any>
>(factory: () => Promise<{ default: T }>): PreloadableLazyComponent<T> {
  // 失敗した Promise は捨てて、次の呼び出しで取り直す。先読みの失敗（オフライン・
  // デプロイ直後の stale chunk）を React.lazy にそのまま渡すと、クリック時に読み直す
  // 機会が無く、そのままエラー画面に落ちる
  let pending: Promise<{ default: T }> | null = null;
  let loaded: { default: T } | null = null;
  const load = (): Promise<{ default: T }> => {
    if (!pending) {
      const attempt = factory().then(mod => {
        loaded = ensureLazyModule(mod);
        return loaded;
      });
      attempt.catch(() => {
        if (pending === attempt) pending = null;
      });
      pending = attempt;
    }
    return pending;
  };
  const Lazy = lazy(() =>
    load().catch((error: unknown) => {
      if (isChunkLoadError(error) && reloadOnceForChunkError()) {
        // リロードが走るまで何も描画させない（Suspense の fallback を出したまま待つ）
        return new Promise<{ default: T }>(() => {});
      }
      throw error;
    })
  );
  const Component = (props: ComponentProps<T>) => {
    // mount の時点で読み込み済みなら Suspense を介さず直接描く。React.lazy は
    // 読み込み済みでも初回だけ suspend するため、先読みした画面でも fallback が
    // 1 フレーム出てしまう。どちらで描くかは mount 時に決めて変えない（途中で
    // 切り替えると要素の型が変わり、画面が作り直される）
    const [directModule] = useState(() => loaded);
    return directModule
      ? createElement(directModule.default, props)
      : createElement(Lazy, props);
  };
  Component.preload = () => {
    prefetchInFlight += 1;
    load()
      .catch(() => {
        // 先読みの失敗は無視する。描画時に load() が取り直し、そこで失敗すれば
        // 上の復帰リロードに乗る
      })
      .finally(() => {
        prefetchInFlight -= 1;
      });
  };
  return Component as PreloadableLazyComponent<T>;
}

/**
 * ページ内でネストして呼ばれる動的 import（例: ChatScreenV2、各種モーダル）も
 * 含め、あらゆるチャンク失敗を拾う安全網。
 *
 * Vite は動的 import を __vitePreload でラップしており、その読み込みに失敗すると
 * window に `vite:preloadError` を dispatch する。ルート単位の lazyWithReload では
 * 拾えないネストされた import 失敗もここで一括して復帰リロードに繋ぐ。
 * main.tsx から一度だけ呼び出す。
 */
export function installChunkReloadHandler(): void {
  if (typeof window === "undefined") return;
  window.addEventListener("vite:preloadError", event => {
    // 先読み中の失敗はここで復帰しない（preload の説明を参照）。描画時の失敗は
    // React.lazy 側の catch が同じ復帰リロードに乗せる
    if (prefetchInFlight > 0) return;
    // reload を開始できたら既定のエラー送出を止める（画面のチラつきを避ける）
    if (reloadOnceForChunkError()) {
      event.preventDefault();
    }
  });
}
